import { useState, useId } from "react";
import { inputClass, labelClass } from "./fieldStyles";

interface InlineInputProps {
  label: string;
  value: string | number;
  onChange: (value: string) => void;
  /** Error message to show when touched and invalid. Null = valid. */
  error?: string | null;
  /** When > 0, marks the field as touched (used by parent "touch all" triggers). */
  touchAllCounter?: number;
  type?: "text" | "number";
  placeholder?: string;
  maxLength?: number;
  min?: number;
  max?: number;
  step?: string;
  inputMode?: "text" | "decimal" | "numeric";
  onBlur?: () => void;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  required?: boolean;
  containerClassName?: string;
  /** Render as read-only-looking, non-interactive. Used for locked "already collected on
   *  another step" fields (e.g. the primary Entity 1 card's Legal Name / EIN). */
  disabled?: boolean;
  /** Small explanatory line rendered under the input and linked via aria-describedby so
   *  screen-reader users hear the explanation when the input receives focus. Used to
   *  explain why a disabled field is locked without needing a separate untied paragraph. */
  hint?: string;
}

/**
 * Controlled text/number input with built-in touch state and error display.
 * Use for array item fields (owners, groups) where `useFormField` doesn't apply.
 *
 * Usage:
 *   <InlineInput label="Last Name" value={owner.lastName} onChange={(v) => update({ lastName: v })}
 *     error={!owner.lastName.trim() ? "Last Name is required" : null} required />
 */
export function InlineInput({
  label,
  value,
  onChange,
  error,
  touchAllCounter = 0,
  type = "text",
  placeholder,
  maxLength,
  min,
  max,
  step,
  inputMode,
  onBlur: onBlurProp,
  onPaste,
  required,
  containerClassName,
  disabled,
  hint,
}: InlineInputProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const [internallyTouched, setInternallyTouched] = useState(false);
  // Touched if the user blurred OR the parent triggered a touch-all sweep.
  const touched = internallyTouched || touchAllCounter > 0;

  const showError = touched ? error : null;
  const state = showError ? "error" : "default";
  // Chain describedby ids — a locked-and-invalid input would announce both.
  const describedBy = [showError ? errorId : null, hint ? hintId : null].filter(Boolean).join(" ") || undefined;

  return (
    <div className={containerClassName}>
      <label htmlFor={id} className={labelClass()}>
        {label}
        {required && <span className="text-danger" aria-hidden="true">&nbsp;*</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        inputMode={inputMode}
        onBlur={() => { setInternallyTouched(true); onBlurProp?.(); }}
        onPaste={onPaste}
        placeholder={placeholder}
        maxLength={maxLength}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        aria-invalid={showError ? true : undefined}
        aria-describedby={describedBy}
        className={`${inputClass(state)}${disabled ? " opacity-60 cursor-not-allowed bg-gray-50" : ""}`}
      />
      {showError && <p id={errorId} className="text-xs text-danger mt-1">{showError}</p>}
      {hint && <p id={hintId} className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}
